import type { CSSProperties } from 'react';
import type { CssSize, ImageSize, ImageStrapiType } from '@/strapi-types';

const getCssDimension = (value?: ImageSize | CssSize) => {
  if (!value) return undefined;
  const stringValue = String(value);
  if (stringValue.includes('%') || stringValue.includes('px')) {
    return stringValue;
  }
  return `${stringValue}%`;
};

const getIntrinsicDimension = (value?: ImageSize | CssSize) => {
  if (!value) return undefined;

  const parsedValue = Number(String(value).replace('px', ''));
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

type BaseImageSizeOptions = {
  image?: ImageStrapiType | null;
  width?: ImageSize;
  height?: ImageSize;
  overrideWidth?: CssSize;
  overrideHeight?: CssSize;
};

/**
 * Resolves the two different size concepts used by BaseImage.
 *
 * - `width` and `height` returned from this helper are intrinsic dimensions for
 *   Next/Image. Explicit override dimensions win, then CMS media metadata, then
 *   the default fallback. The regular BaseImage `width`/`height` values are only
 *   used as an intrinsic fallback when no override or media metadata exists.
 * - `style` is the visual render size. It is created only from explicit
 *   BaseImage sizing props (`width`, `height`, `overrideWidth`, `overrideHeight`)
 *   so CMS metadata does not force images to render at their raw pixel size.
 *
 * Unitless string visual sizes are treated as percentages by the current CMS
 * contract (`"100"` -> `"100%"`). Values that already include `%` or `px` are
 * passed through unchanged.
 */
/**
 * CMS originals can be far wider than any rendered slot. Without a `sizes`
 * prop next/image builds its srcset from the intrinsic width, so an
 * uncapped 4000px original would make browsers download near-original
 * files. 1920 covers the largest full-bleed render at 1x.
 */
const MAX_INTRINSIC_WIDTH = 1920;

const capIntrinsicSize = (width: number, height: number) => {
  if (width <= MAX_INTRINSIC_WIDTH) {
    return { width, height };
  }
  // Scale both dimensions so the aspect ratio next/image derives from
  // width/height stays intact.
  return {
    width: MAX_INTRINSIC_WIDTH,
    height: Math.round((height * MAX_INTRINSIC_WIDTH) / width),
  };
};

export const getBaseImageSize = ({
  image,
  width,
  height,
  overrideWidth,
  overrideHeight,
}: BaseImageSizeOptions) => {
  const styleWidth = getCssDimension(overrideWidth ?? width);
  const styleHeight = getCssDimension(overrideHeight ?? height);
  const intrinsicWidth =
    getIntrinsicDimension(overrideWidth) ??
    image?.width ??
    getIntrinsicDimension(width) ??
    100;
  const intrinsicHeight =
    getIntrinsicDimension(overrideHeight) ??
    image?.height ??
    getIntrinsicDimension(height) ??
    100;
  const cappedSize = capIntrinsicSize(intrinsicWidth, intrinsicHeight);
  const style: CSSProperties | undefined =
    styleWidth || styleHeight
      ? {
          ...(styleWidth ? { width: styleWidth } : { width: 'auto' }),
          ...(styleHeight ? { height: styleHeight } : { height: 'auto' }),
        }
      : undefined;

  return {
    width: cappedSize.width,
    height: cappedSize.height,
    style,
  };
};
